import { Inject, inject, Injectable, signal } from "@angular/core";
import { DataInterface } from "../interfaces/data-interface";
import { HttpClient } from "@angular/common/http";
import { map, Observable, of } from "rxjs";
import { WebSocketService } from "../web-socket-handlers/web-socket.service";
import { environment } from "src/environments/environment";

@Injectable({ providedIn: 'root' })
export abstract class DataStore<T extends DataInterface> {

    protected readonly _data = signal<T[]>([])
    protected readonly _loading = signal(false)
    protected readonly _error = signal<string | null>(null)
    protected readonly _loaded = signal(false)

    readonly data = this._data.asReadonly()
    readonly loading = this._loading.asReadonly()
    readonly error = this._error.asReadonly()
    readonly loaded = this._loaded.asReadonly()

    protected abstract _modelKey: string

    protected http: HttpClient = inject(HttpClient)

    protected socketService: WebSocketService = Inject(WebSocketService)

    protected endpoint: string
    protected baseUrl: string
    protected singular: string
    protected plural: string

    constructor(socketKey: string, apiKey: string, singular: string = '', plural: string = '') {
        this.endpoint = apiKey
        this.baseUrl = `${environment.apiUrl}/${this.endpoint}`
        this.singular = singular || this.pluralToSingular(this.endpoint)
        this.plural = plural || this.endpoint || this.singularToPlural(this.singular)
    }

    protected abstract createObject(data: {} | any): any





    /**
     * Load data from API if not already loaded, and cache in signal.
     */
    load(queryParams: {} = {}): void {
        if (this._loaded()) return;
        this._loading.set(true);
        this.getMany(queryParams).subscribe({
            next: (items: T[]) => {
                this._data.set(items);
                this._loaded.set(true);
                this._error.set(null);
            },
            error: (err) => {
                this._error.set(err?.message || 'Failed to load data');
            },
            complete: () => {
                this._loading.set(false);
            }
        });
    }

    /**
     * Force refresh from API and update cache.
     */
    refresh(queryParams: {} = {}): void {
        this._loading.set(true);
        this.getMany(queryParams).subscribe({
            next: (items: T[]) => {
                this._data.set(items);
                this._loaded.set(true);
                this._error.set(null);
            },
            error: (err) => {
                this._error.set(err?.message || 'Failed to refresh data');
            },
            complete: () => {
                this._loading.set(false);
            }
        });
    }

    /**
     * Listen for websocket events and refresh data on change.
     */
    protected registerSocketRefresh(socketKey: string): void {
        if (!this.socketService) {
            return;
        }
        this.socketService.registerEventHandler(socketKey, (event: any) => {

        });

        // this.socketService.on(socketKey, (event: any) => {
        //     // You may want to filter event types here
        //     this.refresh();
        // });
    }

    /**
     * Call this in your subclass constructor after super()
     * Example: this.registerSocketRefresh('drone-updated')
     */












    get(id: string): Observable<any> {



        return this.http.get(`${this.baseUrl}/${id}`, { observe: 'body' })
                        .pipe(map((_: any) => this.createObject(_.body?.data[this.singular] || _.body?.data)))
    }

    getMany(queryParams: {} = {}): Observable<T[]> {

        return this.http.get(this.baseUrl, { params: queryParams, observe: 'body' }).pipe(map((_: any) => 
        {
            return (_?.data[this.plural] || _?.data || []).map((obj: any) => this.createObject(obj))
        }))

        // return this.http.get(this.baseUrl, { params: queryParams, observe: 'body' }).pipe(map((_: any) => 
        // {
        //     return (_?.data[this.plural] || _?.data || []).map((obj: any) => this.createObject(obj))
        // }))
    }

    update(model: T): Observable<T> {
        const id = (model as any).id
        return this.http.put(`${this.baseUrl}/${id}`, model, { observe: 'response' }).pipe(
            map((_: any) => this.createObject(_.body?.data[this.singular] || _.body.data))
        )
    }

    create(model: T): Observable<T> {
        return this.http.post(this.baseUrl, model, { observe: 'response' }).pipe(
            map((_: any) => this.createObject(_.body?.data[this.singular] || _.body.data))
        )
    }

    delete(id: string): Observable<any> {
        return this.http.delete(`${this.baseUrl}/${id}`, { observe: 'response'})
    }

    


    // Register socket handlers

    

    // Get
    // GetAll
    // Create
    // Update
    // Archive
    // Delete




    private pluralToSingular(plural: string): string {
        // NOTE: We try to handle most common cases here, but this is not exhaustive
        return !plural ? '' : plural
            // Words ending in 'ies' that become 'y' (e.g. 'categories' -> 'category')
            .replace(/(ies)$/, 'y')
            // Words ending in 'ves' that become 'f' (e.g. 'leaves' -> 'leaf')
            .replace(/(ves)$/, 'f')
            // Words ending in 'oes' that become 'o' (e.g. 'potatoes' -> 'potato')
            .replace(/(oes)$/, 'o')
            // Words ending in 'xes' that become 'x' (e.g. 'boxes' -> 'box')
            .replace(/(xes)$/, 'x')
            // Words ending in 'ses' that become 's' (e.g. 'dishes' -> 'dish')
            .replace(/(shes)$/, 'sh')
            // Words ending in 'ches' that become 'ch' (e.g. 'branches' -> 'branch')
            .replace(/(ches)$/, 'ch')
            // Words ending in 'sses' that become 'ss' (e.g. 'bosses' -> 'boss')
            .replace(/(sses)$/, 'ss')
            // Words ending in 's' but not 'ss' that become '' (e.g. 'cats' -> 'cat')
            .replace(/([^s])s$/, '$1');
    }

    private singularToPlural(singular: string): string {
        // NOTE: We try to handle most common cases here, but this is not exhaustive
        return !singular ? '' : singular
            // Words ending in 'y' that become 'ies' (e.g. 'category' -> 'categories')
            .replace(/(y)$/, 'ies')
            // Words ending in 'f' that become 'ves' (e.g. 'leaf' -> 'leaves')
            .replace(/(f)$/, 'ves')
            // Words ending in 'o' that become 'oes' (e.g. 'potato' -> 'potatoes')
            .replace(/(o)$/, 'oes')
            // Words ending in 'x' that become 'xes' (e.g. 'box' -> 'boxes')
            .replace(/(x)$/, 'xes')
            // Words ending in 'sh' that become 'ses' (e.g. 'dish' -> 'dishes')
            .replace(/(sh)$/, 'ses')
            // Words ending in 'ch' that become 'ches' (e.g. 'branch' -> 'branches')
            .replace(/(ch)$/, 'ches')
            // Words ending in 'ss' that become 'sses' (e.g. 'boss' -> 'bosses')
            .replace(/(ss)$/, 'sses')
            // Words not ending in 's' that become 's' (e.g. 'cat' -> 'cats')
            .replace(/([^s])$/, '$1s');
    }
}
